export const PAYMENT_RETURN_QUERY_PARAMS = [
  'cf_currency',
  'cf_is_free_trial',
  'cf_subscription_id',
  'cf_subscription_type',
  'cf_payment_use_case',
  'cf_tax',
  'cf_user_id',
  'city',
  'country',
  'email',
  'is_free_trial_autostart_success',
  'is_subscription_autostart_success',
  'line1',
  'line2',
  'name',
  'payment_intent',
  'payment_intent_client_secret',
  'postalCode',
  'redirect_status',
  'setup_intent',
  'setup_intent_client_secret',
  'state',
  'cf_promo_code',
  'cf_stripe_account',
] as const

const paymentParams = new Set<string>(PAYMENT_RETURN_QUERY_PARAMS)

export function sanitizePaymentReturnUrl(value: string): string {
  const queryStart = value.indexOf('?')
  if (queryStart === -1) {
    return value
  }
  const hashStart = value.indexOf('#', queryStart)
  const queryEnd = hashStart === -1 ? value.length : hashStart
  const query = value.slice(queryStart + 1, queryEnd)
  const parts = query.split('&')
  const retained = parts.filter(part => {
    const key = new URLSearchParams(part).keys().next().value
    return key === undefined || !paymentParams.has(key)
  })
  if (retained.length === parts.length) {
    return value
  }
  const search = retained.join('&')
  return value.slice(0, queryStart) + (search ? `?${search}` : '') + value.slice(queryEnd)
}

/** Scrub URL copies in telemetry, including nested contexts and breadcrumbs. */
export function sanitizePaymentTelemetry<T>(value: T): T {
  const copies = new WeakMap<object, unknown>()
  function sanitize(input: unknown): unknown {
    if (typeof input === 'string') {
      return /^(?:[a-z][a-z\d+.-]*:\/\/|\/|\?)/i.test(input)
        ? sanitizePaymentReturnUrl(input)
        : input
    }
    if (!input || typeof input !== 'object') {
      return input
    }
    if (copies.has(input)) {
      return copies.get(input)
    }
    if (Array.isArray(input)) {
      const copy: unknown[] = []
      copies.set(input, copy)
      for (const item of input) {
        copy.push(sanitize(item))
      }
      return copy
    }
    const prototype = Object.getPrototypeOf(input)
    if (prototype !== null && prototype.constructor?.name !== 'Object') {
      return input
    }
    const copy: Record<string, unknown> = {}
    copies.set(input, copy)
    for (const [key, item] of Object.entries(input)) {
      Object.defineProperty(copy, key, {
        value: sanitize(item),
        enumerable: true,
        writable: true,
        configurable: true,
      })
    }
    return copy
  }
  return sanitize(value) as T
}
